import { useEffect, useState, type ReactNode } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ConfirmDialog } from '../../components/ConfirmDialog';
import { PageBody } from '../../components/Page';
import { Badge } from '../../components/Pill';
import { useShopFile } from '../../components/ShopFileLayout';
import { StatusBanner } from '../../components/StatusBanner';
import { intlLocale } from '../../i18n/formats';
import { useLocale } from '../../i18n/LocaleProvider';
import type { SupportedLocale } from '../../i18n';
import { isShopAccessRequired, opsErrorMessage } from '../../lib/ops-api';
import {
  fetchShopDatev,
  resendDatevInvite,
  resendRecipientConfirmation,
  type OpsDatevExportJob,
  type OpsDatevInvite,
  type OpsDatevInviteResendResult,
  type OpsExportSchedule,
  type OpsRecipientResendResult,
  type OpsScheduleRecipient,
} from '../../lib/ops-datev-api';
import {
  exportStatusTone,
  inviteResendOffer,
  inviteResendWarnings,
  inviteStatusLabelKey,
  inviteStatusTone,
  isRateLimited,
  lockStateLabelKey,
  lockStateTone,
  pruefpunktTone,
  recipientStateHintKey,
  recipientStateLabelKey,
  recipientStateTone,
} from '../../lib/ops-datev';
import { REASON_MIN_LENGTH, isReasonAcceptable } from '../../lib/shop-file';

/** A date-time as the reader's locale writes it, or a dash. */
function when(
  value: string | null | undefined,
  locale: SupportedLocale,
): string {
  if (!value) return '—';
  return new Date(value).toLocaleString(intlLocale(locale), {
    dateStyle: 'medium',
    timeStyle: 'short',
  });
}

/** A date as the reader's locale writes it. */
function day(value: string, locale: SupportedLocale): string {
  return new Date(value).toLocaleDateString(intlLocale(locale));
}

/** One labelled fact in a definition grid. */
function Fact({
  label,
  children,
}: {
  readonly label: string;
  readonly children: ReactNode;
}) {
  return (
    <div className="min-w-0">
      <dt className="text-xs font-medium text-gray-500">{label}</dt>
      <dd className="text-sm text-gray-900 break-words">{children}</dd>
    </div>
  );
}

/** A card with a heading. */
function Section({
  title,
  children,
}: {
  readonly title: string;
  readonly children: ReactNode;
}) {
  return (
    <section className="card space-y-3">
      <h2 className="text-base font-semibold text-gray-900">{title}</h2>
      {children}
    </section>
  );
}

/** Yes/no as a badge. */
function YesNo({ value }: { readonly value: boolean }) {
  const { t } = useLocale();
  return (
    <Badge tone={value ? 'green' : 'gray'}>
      {value ? t('common.yes') : t('common.no')}
    </Badge>
  );
}

/** What the confirm step is about to resend. */
type ResendTarget =
  | { readonly kind: 'invite'; readonly invite: OpsDatevInvite }
  | { readonly kind: 'recipient'; readonly recipient: OpsScheduleRecipient };

/** What came back from a resend, for the banner above the tab. */
type ResendOutcome =
  | { readonly kind: 'invite'; readonly result: OpsDatevInviteResendResult }
  | { readonly kind: 'recipient'; readonly result: OpsRecipientResendResult };

/**
 * Steuerberater-portal support (work order item 8): the invite, the export recipients, the last
 * DATEV exports — and the two resends.
 *
 * **Resending is ALL support may do here** (AC-8.4). Creating, withdrawing, reclaiming or
 * re-inviting move the merchant's settings lock and are the merchant's decisions; the tab says so
 * rather than offering them. `DATEV_INVITE_UNDELIVERED` is shown, never resolved (AC-G1a) — it
 * closes itself when a resend gets out.
 */
export function DatevTab() {
  const { t, locale } = useLocale();
  const { shopId, requireAccess, reload } = useShopFile();
  const queryClient = useQueryClient();
  const [target, setTarget] = useState<ResendTarget | null>(null);
  const [reason, setReason] = useState('');
  const [outcome, setOutcome] = useState<ResendOutcome | null>(null);

  const overview = useQuery({
    queryKey: ['shop-datev', shopId],
    queryFn: () => fetchShopDatev(shopId),
    retry: false,
  });

  const readRefused =
    overview.isError && isShopAccessRequired(overview.error, shopId);
  useEffect(() => {
    if (readRefused) requireAccess();
  }, [readRefused, requireAccess]);

  const resend = useMutation({
    mutationFn: async (input: {
      readonly target: ResendTarget;
      readonly reason: string;
    }): Promise<ResendOutcome> =>
      input.target.kind === 'invite'
        ? {
            kind: 'invite',
            result: await resendDatevInvite(
              shopId,
              input.target.invite.id,
              input.reason,
            ),
          }
        : {
            kind: 'recipient',
            result: await resendRecipientConfirmation(
              shopId,
              input.target.recipient.id,
              input.reason,
            ),
          },
    onSuccess: (result) => {
      setOutcome(result);
      setTarget(null);
      setReason('');
      void queryClient.invalidateQueries({ queryKey: ['shop-datev', shopId] });
      // A delivered invite closes DATEV_INVITE_UNDELIVERED, which the file's overview lists.
      reload();
    },
    onError: (error) => {
      setTarget(null);
      if (isShopAccessRequired(error, shopId)) requireAccess();
    },
  });

  function openConfirm(next: ResendTarget): void {
    resend.reset();
    setOutcome(null);
    setReason('');
    setTarget(next);
  }

  if (overview.isLoading || !overview.data) {
    return (
      <PageBody>
        {overview.isError ? (
          <StatusBanner tone="error">
            {opsErrorMessage(overview.error, t('opsDatev.loadFailed'))}
          </StatusBanner>
        ) : (
          <StatusBanner tone="loading">{t('common.loading')}</StatusBanner>
        )}
      </PageBody>
    );
  }

  const data = overview.data;
  const settings = data.settings;

  return (
    <PageBody>
      <StatusBanner tone="info">{t('opsDatev.onlyResend')}</StatusBanner>

      {outcome && <OutcomeBanner outcome={outcome} />}
      {resend.isError && (
        <StatusBanner tone="error">
          {isRateLimited(resend.error)
            ? t('opsDatev.rateLimited')
            : opsErrorMessage(resend.error, t('opsDatev.resendFailed'))}
        </StatusBanner>
      )}

      <Section title={t('opsDatev.settings.title')}>
        <dl className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Fact label={t('opsDatev.settings.lock')}>
            <Badge
              tone={lockStateTone(settings.lockState)}
              title={settings.lockState}
            >
              {t(lockStateLabelKey(settings.lockState))}
            </Badge>
          </Fact>
          <Fact label={t('opsDatev.settings.editingLocked')}>
            <YesNo value={settings.editingLocked} />
          </Fact>
          <Fact label={t('opsDatev.settings.configured')}>
            {settings.configured
              ? when(settings.updatedAt, locale)
              : t('opsDatev.settings.defaults')}
          </Fact>
          <Fact label={t('opsDatev.settings.skr')}>
            {settings.skr} · {settings.sachkontenlaenge}
          </Fact>
          <Fact label={t('opsDatev.settings.berater')}>
            <YesNo value={settings.hasBerater} />
          </Fact>
          <Fact label={t('opsDatev.settings.mandant')}>
            <YesNo value={settings.hasMandant} />
          </Fact>
          <Fact label={t('opsDatev.settings.belegbilder')}>
            <YesNo value={settings.belegbilder} />
          </Fact>
          <Fact label={t('opsDatev.settings.bookPayments')}>
            <YesNo value={settings.bookPayments} />
          </Fact>
        </dl>
      </Section>

      <Section title={t('opsDatev.invites.title')}>
        <p className="text-sm text-gray-600">{t('opsDatev.invites.lead')}</p>
        {data.invites.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="data-table">
              <thead>
                <tr>
                  <th>{t('opsDatev.invites.columnAccountant')}</th>
                  <th>{t('opsDatev.invites.columnStatus')}</th>
                  <th>{t('opsDatev.invites.columnDelivery')}</th>
                  <th>{t('opsDatev.invites.columnLink')}</th>
                  <th>{t('opsDatev.invites.columnCreated')}</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {data.invites.map((invite) => (
                  <InviteRow
                    key={invite.id}
                    invite={invite}
                    busy={resend.isPending}
                    onResend={() => openConfirm({ kind: 'invite', invite })}
                  />
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <p className="text-sm text-gray-600">{t('opsDatev.invites.none')}</p>
        )}
      </Section>

      <Section title={t('opsDatev.schedules.title')}>
        <p className="text-sm text-gray-600">{t('opsDatev.schedules.lead')}</p>
        {data.schedules.length > 0 ? (
          data.schedules.map((schedule) => (
            <ScheduleBlock
              key={schedule.id}
              schedule={schedule}
              busy={resend.isPending}
              onResend={(recipient) =>
                openConfirm({ kind: 'recipient', recipient })
              }
            />
          ))
        ) : (
          <p className="text-sm text-gray-600">
            {t('opsDatev.schedules.none')}
          </p>
        )}
      </Section>

      <Section title={t('opsDatev.exports.title')}>
        {data.exports.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="data-table">
              <thead>
                <tr>
                  <th>{t('opsDatev.exports.columnCreated')}</th>
                  <th>{t('opsDatev.exports.columnPeriod')}</th>
                  <th>{t('opsDatev.exports.columnStatus')}</th>
                  <th>{t('opsDatev.exports.columnMail')}</th>
                  <th>{t('opsDatev.exports.columnPruefliste')}</th>
                </tr>
              </thead>
              <tbody>
                {data.exports.map((job) => (
                  <ExportRow key={job.id} job={job} />
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <p className="text-sm text-gray-600">{t('opsDatev.exports.none')}</p>
        )}
      </Section>

      <ConfirmDialog
        open={target !== null}
        title={
          target?.kind === 'invite'
            ? t('opsDatev.confirm.invite.title')
            : t('opsDatev.confirm.recipient.title')
        }
        confirmLabel={t('opsDatev.confirm.submit')}
        confirmDisabled={!isReasonAcceptable(reason) || resend.isPending}
        onCancel={() => setTarget(null)}
        onConfirm={() => {
          if (target && isReasonAcceptable(reason)) {
            resend.mutate({ target, reason: reason.trim() });
          }
        }}
      >
        {target && (
          <div className="space-y-3">
            <ConfirmBody target={target} />
            <label htmlFor="opsDatevReason" className="field-label">
              {t('opsDatev.confirm.reasonLabel')}
            </label>
            <textarea
              id="opsDatevReason"
              value={reason}
              onChange={(event) => setReason(event.target.value)}
              rows={3}
              maxLength={500}
              placeholder={t('opsDatev.confirm.reasonPlaceholder')}
              className="field-input"
            />
            <p className="field-hint">
              {t('opsDatev.confirm.reasonHint', { min: REASON_MIN_LENGTH })}
            </p>
          </div>
        )}
      </ConfirmDialog>
    </PageBody>
  );
}

/** One invite row. The resend button only where the API says the row is resendable. */
function InviteRow({
  invite,
  busy,
  onResend,
}: {
  readonly invite: OpsDatevInvite;
  readonly busy: boolean;
  readonly onResend: () => void;
}) {
  const { t, tCount, locale } = useLocale();
  const offer = inviteResendOffer(invite);

  return (
    <tr>
      <td className="align-top">
        <div className="font-mono text-xs">{invite.accountantEmail}</div>
        {invite.destinationEmail && (
          <div className="text-xs text-gray-500">
            {t('opsDatev.invites.destination', {
              email: invite.destinationEmail,
              cadence: invite.cadence ?? '—',
            })}
          </div>
        )}
      </td>
      <td className="align-top space-y-1">
        <Badge tone={inviteStatusTone(invite.status)} title={invite.status}>
          {t(inviteStatusLabelKey(invite.status))}
        </Badge>
        {invite.finalisedAt && (
          <div className="text-xs text-gray-500">
            {t('opsDatev.invites.finalisedAt', {
              at: when(invite.finalisedAt, locale),
            })}
          </div>
        )}
        {invite.reclaimedAt && (
          <div className="text-xs text-gray-500">
            {t('opsDatev.invites.reclaimedAt', {
              at: when(invite.reclaimedAt, locale),
            })}
          </div>
        )}
      </td>
      <td className="align-top space-y-1">
        {invite.sentAt ? (
          <div className="text-sm">{when(invite.sentAt, locale)}</div>
        ) : (
          <Badge tone="red">{t('opsDatev.invites.notSent')}</Badge>
        )}
        {invite.resendCount > 0 && (
          <div className="text-xs text-gray-500">
            {tCount('opsDatev.invites.resends', invite.resendCount)}
          </div>
        )}
        {invite.lastSendError && (
          <div className="text-xs text-red-700 max-w-xs break-words">
            {t('opsDatev.invites.lastError', {
              at: when(invite.lastSendErrorAt, locale),
              error: invite.lastSendError,
            })}
          </div>
        )}
        {invite.undeliveredItemOpen && (
          <div className="space-y-1">
            <Badge tone="amber" title="DATEV_INVITE_UNDELIVERED">
              {t('opsDatev.invites.undeliveredOpen')}
            </Badge>
            <p className="text-xs text-gray-500 max-w-xs">
              {t('opsDatev.invites.undeliveredHint')}
            </p>
          </div>
        )}
      </td>
      <td className="align-top text-sm">
        {invite.tokenAcceptedAt
          ? t('opsDatev.invites.linkUsed', {
              at: when(invite.tokenAcceptedAt, locale),
            })
          : t('opsDatev.invites.linkUnused')}
      </td>
      <td className="align-top whitespace-nowrap">
        {when(invite.createdAt, locale)}
      </td>
      <td className="align-top">
        {offer.offered ? (
          <button
            type="button"
            className="btn-secondary btn-sm"
            disabled={busy}
            onClick={onResend}
          >
            {t('opsDatev.invites.resend')}
          </button>
        ) : (
          offer.whyKey && (
            <p className="text-xs text-gray-500 max-w-xs">{t(offer.whyKey)}</p>
          )
        )}
      </td>
    </tr>
  );
}

/** One schedule with its recipients. */
function ScheduleBlock({
  schedule,
  busy,
  onResend,
}: {
  readonly schedule: OpsExportSchedule;
  readonly busy: boolean;
  readonly onResend: (recipient: OpsScheduleRecipient) => void;
}) {
  const { t, locale } = useLocale();

  return (
    <div className="rounded-lg border border-gray-200 p-4 space-y-3">
      <div className="flex flex-wrap items-center gap-2 text-sm">
        <span className="font-mono text-xs">
          {schedule.format} · {schedule.exportType}
        </span>
        <Badge tone={schedule.enabled ? 'green' : 'gray'}>
          {schedule.enabled
            ? t('opsDatev.schedules.enabled')
            : t('opsDatev.schedules.disabled')}
        </Badge>
        {schedule.createdByInviteId && (
          <Badge tone="teal">{t('opsDatev.schedules.byAccountant')}</Badge>
        )}
        <span className="text-gray-500">
          {t('opsDatev.schedules.next', {
            at: when(schedule.nextExecution, locale),
          })}
        </span>
      </div>
      {schedule.recipients.length > 0 ? (
        <div className="overflow-x-auto">
          <table className="data-table">
            <thead>
              <tr>
                <th>{t('opsDatev.schedules.columnRecipient')}</th>
                <th>{t('opsDatev.schedules.columnState')}</th>
                <th>{t('opsDatev.schedules.columnSent')}</th>
                <th>{t('opsDatev.schedules.columnConfirmed')}</th>
                <th>{t('opsDatev.schedules.columnExpires')}</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {schedule.recipients.map((recipient) => (
                <tr key={recipient.id}>
                  <td className="align-top font-mono text-xs">
                    {recipient.email}
                  </td>
                  <td className="align-top space-y-1">
                    <Badge
                      tone={recipientStateTone(recipient.confirmationState)}
                      title={`${recipient.confirmationStatus} · ${recipient.confirmationState}`}
                    >
                      {t(recipientStateLabelKey(recipient.confirmationState))}
                    </Badge>
                    <p className="text-xs text-gray-500 max-w-xs">
                      {t(recipientStateHintKey(recipient.confirmationState))}
                    </p>
                  </td>
                  <td className="align-top whitespace-nowrap">
                    {when(recipient.confirmationSentAt, locale)}
                  </td>
                  <td className="align-top whitespace-nowrap">
                    {when(recipient.confirmedAt, locale)}
                  </td>
                  <td className="align-top whitespace-nowrap">
                    {when(recipient.confirmationExpiresAt, locale)}
                  </td>
                  <td className="align-top">
                    {recipient.resendConfirmationAllowed && (
                      <button
                        type="button"
                        className="btn-secondary btn-sm"
                        disabled={busy}
                        onClick={() => onResend(recipient)}
                      >
                        {t('opsDatev.schedules.resend')}
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <p className="text-sm text-gray-600">
          {t('opsDatev.schedules.noRecipients')}
        </p>
      )}
    </div>
  );
}

/** One DATEV export job. */
function ExportRow({ job }: { readonly job: OpsDatevExportJob }) {
  const { t, tCount, locale } = useLocale();

  return (
    <tr>
      <td className="align-top whitespace-nowrap">
        {when(job.createdAt, locale)}
      </td>
      <td className="align-top whitespace-nowrap">
        {day(job.fromDate, locale)} – {day(job.toDate, locale)}
        <div className="font-mono text-xs text-gray-500">{job.exportType}</div>
      </td>
      <td className="align-top space-y-1">
        <Badge tone={exportStatusTone(job.status)}>{job.status}</Badge>
        {job.errorMessage && (
          <div className="text-xs text-red-700 max-w-xs break-words">
            {job.errorMessage}
          </div>
        )}
      </td>
      <td className="align-top text-sm">
        {job.emailSent
          ? tCount('opsDatev.exports.mailed', job.recipientCount)
          : t('opsDatev.exports.notMailed')}
      </td>
      <td className="align-top">
        {job.pruefliste === null || job.pruefliste === undefined ? (
          <span className="text-xs text-gray-500">
            {t('opsDatev.exports.noPruefliste')}
          </span>
        ) : job.pruefliste.length === 0 ? (
          <Badge tone="green">{t('opsDatev.exports.prueflisteClean')}</Badge>
        ) : (
          <div className="flex flex-wrap gap-1">
            {job.pruefliste.map((entry) => (
              <Badge
                key={entry.code}
                tone={pruefpunktTone(entry.severity)}
                title={entry.severity}
              >
                <span className="font-mono">{entry.code}</span>&nbsp;×
                {entry.count}
              </Badge>
            ))}
          </div>
        )}
      </td>
    </tr>
  );
}

/** What the confirm step says before a resend — the link it is about to kill, above all. */
function ConfirmBody({ target }: { readonly target: ResendTarget }) {
  const { t } = useLocale();

  if (target.kind === 'recipient') {
    return (
      <>
        <p>
          {t('opsDatev.confirm.recipient.body', {
            email: target.recipient.email,
          })}
        </p>
        <StatusBanner tone="warning">
          {t('opsDatev.confirm.recipient.linkStops')}
        </StatusBanner>
      </>
    );
  }

  const warnings = inviteResendWarnings(target.invite.tokenAcceptedAt != null);
  return (
    <>
      <p>
        {t('opsDatev.confirm.invite.body', {
          email: target.invite.accountantEmail,
        })}
      </p>
      <StatusBanner tone={warnings.tone}>
        <span className="space-y-1 block">
          {warnings.keys.map((key) => (
            <span key={key} className="block">
              {t(key)}
            </span>
          ))}
        </span>
      </StatusBanner>
      <p className="text-xs text-gray-500">
        {t('opsDatev.confirm.invite.smtpNote')}
      </p>
    </>
  );
}

/** The result of the last resend: sent or not, the audit row, and the link that is now dead. */
function OutcomeBanner({ outcome }: { readonly outcome: ResendOutcome }) {
  const { t } = useLocale();

  if (outcome.kind === 'recipient') {
    const { result } = outcome;
    return (
      <StatusBanner tone={result.sent ? 'success' : 'error'}>
        <span className="block">
          {result.sent
            ? t('opsDatev.outcome.recipientSent', {
                email: result.recipient.email,
              })
            : t('opsDatev.outcome.recipientFailed', {
                email: result.recipient.email,
                message: result.message,
              })}
        </span>
        <span className="block">{t('opsDatev.outcome.recipientLinkDead')}</span>
        <span className="block font-mono text-xs">
          {t('opsDatev.outcome.auditId', { id: result.auditLogId })}
        </span>
      </StatusBanner>
    );
  }

  const { result } = outcome;
  return (
    <StatusBanner tone={result.sent ? 'success' : 'error'}>
      <span className="block">
        {result.sent
          ? t('opsDatev.outcome.inviteSent', {
              email: result.invite.accountantEmail,
            })
          : t('opsDatev.outcome.inviteFailed', {
              error: result.invite.lastSendError ?? '—',
            })}
      </span>
      <span className="block">
        {result.previousLinkWasUsed
          ? t('opsDatev.outcome.inviteLinkDeadUsed')
          : t('opsDatev.outcome.inviteLinkDead')}
      </span>
      <span className="block font-mono text-xs">
        {t('opsDatev.outcome.auditId', { id: result.auditLogId })}
      </span>
    </StatusBanner>
  );
}
